// MF-568 — What opens under a PAT chart when something is selected: one clean card with a title, how many students,
// a "Clear selection" action and either a table or a few key facts. Every PAT card uses it, so the gap to the chart above,
// the header, the columns and the scrolling look and behave the same everywhere.
import { Box, Button, Chip, Typography } from '@mui/material'
import { SUMMARY_PALETTE } from '../boardTheme'

/**
 * @param {{ title: string, count?: number, noun?: string, onClear?: Function, hint?: string, children: React.ReactNode }} props
 */
export const DetailCard = ({ title, count, noun = 'student', onClear, hint, children }) => (
  <Box
    aria-live='polite'
    sx={{
      mt: 2,
      border: `1px solid ${SUMMARY_PALETTE.grid}`,
      borderRadius: '12px',
      bgcolor: '#fff',
      overflow: 'hidden',
      boxShadow: '0 1px 2px rgba(20,40,70,.04)'
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.75,
        py: 1,
        bgcolor: '#f6f9fc',
        borderBottom: `1px solid ${SUMMARY_PALETTE.grid}`,
        flexWrap: 'wrap'
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: SUMMARY_PALETTE.ink }}>{title}</Typography>
      {Number.isFinite(count) ? (
        <Chip
          size='small'
          label={`${count} ${noun}${count === 1 ? '' : 's'}`}
          sx={{ height: 20, fontSize: 11 }}
        />
      ) : null}
      {hint ? <Typography sx={{ fontSize: 11.5, color: SUMMARY_PALETTE.muted }}>{hint}</Typography> : null}
      <Box sx={{ flex: 1 }} />
      {onClear ? (
        <Button size='small' onClick={onClear} sx={{ fontSize: 12, textTransform: 'none', minWidth: 0 }}>
          Clear selection
        </Button>
      ) : null}
    </Box>
    {children}
  </Box>
)

/** A table with a sticky header, zebra rows and a bounded height, for the students of a selection. */
export const DetailTable = ({ columns, rows, rowKey, maxHeight = 280 }) => (
  <Box sx={{ maxHeight, overflow: 'auto' }}>
    <Box component='table' sx={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
      <thead>
        <tr>
          {columns.map((c) => (
            <Box
              key={c.key}
              component='th'
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                bgcolor: '#fff',
                textAlign: c.align || 'left',
                px: 1.75,
                py: 0.75,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '.03em',
                textTransform: 'uppercase',
                color: SUMMARY_PALETTE.faint,
                borderBottom: `1px solid ${SUMMARY_PALETTE.grid}`,
                whiteSpace: 'nowrap'
              }}
            >
              {c.label}
            </Box>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <Box key={rowKey(r)} component='tr' sx={{ bgcolor: i % 2 ? '#fafbfd' : '#fff' }}>
            {columns.map((c) => (
              <Box
                key={c.key}
                component='td'
                sx={{
                  px: 1.75,
                  py: 0.75,
                  textAlign: c.align || 'left',
                  color: c.muted ? SUMMARY_PALETTE.muted : SUMMARY_PALETTE.ink,
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: c.bold ? 600 : 400,
                  whiteSpace: c.wrap ? 'normal' : 'nowrap'
                }}
              >
                {c.render ? c.render(r) : r[c.key]}
              </Box>
            ))}
          </Box>
        ))}
      </tbody>
    </Box>
  </Box>
)

/** One key fact of a selection: a small label over a value (and an optional line under it). */
export const Fact = ({ label, value, sub, tone }) => (
  <Box
    sx={{
      flex: '1 1 150px',
      minWidth: 0,
      p: 1.25,
      borderRadius: '10px',
      bgcolor: '#f6f9fc',
      border: '1px solid #e6edf4'
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.03em',
        textTransform: 'uppercase',
        color: SUMMARY_PALETTE.faint
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: 16, fontWeight: 700, color: tone || SUMMARY_PALETTE.ink, lineHeight: 1.3 }}>
      {value}
    </Typography>
    {sub ? <Typography sx={{ fontSize: 11.5, color: SUMMARY_PALETTE.muted }}>{sub}</Typography> : null}
  </Box>
)

export const FactRow = ({ children }) => (
  <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', p: 1.75 }}>{children}</Box>
)
